import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchGuaranteesApplication,
  generateGuaranteesConclusionAttachment,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  GuaranteesFetchStatus,
  type TGuaranteesApplication,
  type TGuaranteesApplicationFile,
  type TGuaranteesApprovalCommentResponse,
} from '~/store/guarantees/types.ts';
import type { AppDispatch } from '~/store/store.ts';
import {
  UDBTF_APPROVAL_LETTER_MISSING_VERSION_TEXT,
} from './constants.ts';
import type { TInternalApprovalLetterConfig, TInternalApprovalType } from './types.ts';
import { useInternalApprovalConclusionPdfPreview } from './useInternalApprovalConclusionPdfPreview.ts';
import { getInternalApprovalLetterFile } from './utils.ts';

type TUseInternalApprovalConclusionAttachmentParams = {
  application?: TGuaranteesApplication | null;
  applicationFiles: TGuaranteesApplicationFile[];
  approvalComment?: TGuaranteesApprovalCommentResponse | null;
  approvalConfig: TInternalApprovalLetterConfig;
  approvalType: TInternalApprovalType;
  contragentId: string;
  dispatch: AppDispatch;
  effectiveApplicationId: string;
  generateConclusionAttachmentStatus: GuaranteesFetchStatus;
  hasApplicationFilesLoaded: boolean;
  isApplicationFilesLoading: boolean;
  refreshApplicationFiles: () => Promise<TGuaranteesApplicationFile[]>;
  setModalAlert: (value: { text: string }) => void;
  showGuaranteesRequestError: (error: unknown) => void;
};

export const useInternalApprovalConclusionAttachment = ({
  application,
  applicationFiles,
  approvalComment,
  approvalConfig,
  approvalType,
  contragentId,
  dispatch,
  effectiveApplicationId,
  generateConclusionAttachmentStatus,
  hasApplicationFilesLoaded,
  isApplicationFilesLoading,
  refreshApplicationFiles,
  setModalAlert,
  showGuaranteesRequestError,
}: TUseInternalApprovalConclusionAttachmentParams) => {
  const [additionalDescription, setAdditionalDescription] = useState('');
  const appliedApprovalCommentKeyRef = useRef('');
  const conclusionAttachmentFile = useMemo(
    () =>
      approvalConfig.conclusionDocument
        ? getInternalApprovalLetterFile(
            applicationFiles,
            approvalConfig.conclusionDocument.fileType
          )
        : null,
    [applicationFiles, approvalConfig.conclusionDocument]
  );
  const {
    handleOpenPdf: handleOpenConclusionPdf,
    isDocumentLoading: isConclusionDocumentLoading,
    pdfUrl: conclusionPdfUrl,
  } = useInternalApprovalConclusionPdfPreview({
    conclusionAttachmentFile,
    hasApplicationFilesLoaded,
    isApplicationFilesLoading,
    setModalAlert,
    showGuaranteesRequestError,
  });
  const handleAdditionalDescriptionConfirm = useCallback(async () => {
    if (approvalType !== 'uo' && approvalType !== 'umvn') {
      return;
    }

    const version = application?.version;

    if (typeof version !== 'number') {
      setModalAlert({ text: UDBTF_APPROVAL_LETTER_MISSING_VERSION_TEXT });

      return;
    }

    try {
      await dispatch(
        generateGuaranteesConclusionAttachment({
          applicationId: effectiveApplicationId,
          approvalType,
          comment: additionalDescription,
          version,
        })
      ).unwrap();
      await refreshApplicationFiles();

      if (contragentId) {
        void dispatch(
          fetchGuaranteesApplication({
            applicationId: effectiveApplicationId,
            contragentId,
          })
        );
      }
    } catch (error) {
      showGuaranteesRequestError(error);
    }
  }, [
    additionalDescription,
    application?.version,
    approvalType,
    contragentId,
    dispatch,
    effectiveApplicationId,
    refreshApplicationFiles,
    setModalAlert,
    showGuaranteesRequestError,
  ]);
  const isConclusionGenerating =
    generateConclusionAttachmentStatus === GuaranteesFetchStatus.Loading;
  const isConclusionAttachmentRequired = !!approvalConfig.conclusionDocument;

  useEffect(() => {
    if (approvalType !== 'uo' && approvalType !== 'umvn') {
      return;
    }

    const approvalCommentKey = `${effectiveApplicationId}:${approvalType}`;

    if (!effectiveApplicationId || appliedApprovalCommentKeyRef.current === approvalCommentKey) {
      return;
    }

    if (!approvalComment) {
      return;
    }

    if (
      approvalComment.applicationId !== effectiveApplicationId ||
      approvalComment.approvalType !== approvalType
    ) {
      return;
    }

    setAdditionalDescription(String(approvalComment.comment ?? ''));
    appliedApprovalCommentKeyRef.current = approvalCommentKey;
  }, [approvalComment, approvalType, effectiveApplicationId]);

  return {
    additionalDescription,
    conclusionAttachmentFile,
    conclusionPdfUrl,
    handleAdditionalDescriptionConfirm,
    handleOpenConclusionPdf,
    isConclusionAttachmentRequired,
    isConclusionDocumentLoading,
    isConclusionGenerating,
    setAdditionalDescription,
  };
};
